import { CreditCard } from 'lucide-react';
import BottomButtons from '@/ui/modal/bottom-buttons/BottomButtons';
import Chip from '@/ui/chip/Chip';
import { FloatingTextinput } from '@/ui/floating-textinput/floating-textinput';
import { twColors } from '@/utils/tw-colors';
import { PAYMENT_TYPES } from '@/utils/constants';
import { DelaySelector } from './DelaySelector';
import s from '@/components/orders/new-order/live.module.css';
import { DateTime } from 'luxon';
import { cn } from '@/utils/merge';
import { canSeeShopFees } from '@/utils/funcs';

export const OrderFormFields = ({
	selectedClient,
	asSimpleOrder,
	requiredOrderFields,
	price,
	setPrice,
	validatePrice,
	errorPrice,
	size,
	paymentType,
	setPaymentType,
	comments,
	setComments,
	areas,
	isAutoArea,
	pinArea,
	selectedAreaIdx,
	setSelectedAreaIdx,
	areaPriceProm,
	setAreaPriceProm,
	areaShopFee,
	setAreaShopFee,
	selectedShop,
	isMasterOrAdmin,
	delay,
	delayRef,
	onDelaySelect,
	delayOptionsFiltered,
	isOperating,
	user,
	submitOrder,
	saveDisabled,
	isSendingOrder,
	notOperatingReason,
}) => {
	const { scheduleOperateEveryday, scheduleOperateEverydayTime } = user?.cityLocation || {};

	const willStartOperatingAt =
		scheduleOperateEveryday?.open && scheduleOperateEverydayTime?.open
			? DateTime.fromISO(scheduleOperateEverydayTime.open)
			: null;

	const willStopOperatingAt =
		scheduleOperateEveryday?.close && scheduleOperateEverydayTime?.close
			? DateTime.fromISO(scheduleOperateEverydayTime.close)
			: null;

	// const closeTimeRaw =
	// 	scheduleOperateEveryday?.close && scheduleOperateEverydayTime?.close
	// 		? DateTime.fromISO(scheduleOperateEverydayTime.close)
	// 		: null;

	// helper: seconds since midnight, ignoring date entirely
	// const toSecondsOfDay = dt => dt.hour * 3600 + dt.minute * 60 + dt.second;

	const isWithinNextMinutes = (closeTime, mins) => {
		if (!closeTime?.isValid) return undefined;
		const now = DateTime.now();
		const toSec = dt => dt.hour * 3600 + dt.minute * 60 + dt.second;
		let diff = toSec(closeTime) - toSec(now);
		if (diff < 0) diff += 86400;
		return diff > 0 && diff <= mins * 60;
	};

	const formatHM = dt => (dt?.isValid ? dt.toFormat('HH:mm') : '-:-');

	const startIsWithinNext5Min = isWithinNextMinutes(willStartOperatingAt, 5);
	const startIsWithinNext20Min = isWithinNextMinutes(willStartOperatingAt, 20);

	const stopIsWithinNext5Min = isWithinNextMinutes(willStopOperatingAt, 5);
	const stopIsWithinNext20Min = isWithinNextMinutes(willStopOperatingAt, 20);

	// The two things the label below can say. It used to be rendered whenever
	// either MIGHT apply and then decide its contents on narrower tests, so a
	// paused city with no reason written — or a schedule with only one end set —
	// drew an empty box holding 34px of margin and a 22px line. Deciding the two
	// strings first means the box exists exactly when it has something to say.
	const scheduleNotice =
		(scheduleOperateEveryday?.open &&
			scheduleOperateEverydayTime?.open &&
			startIsWithinNext20Min) ||
		(scheduleOperateEveryday?.close && scheduleOperateEverydayTime?.close && stopIsWithinNext20Min)
			? [
					'Ωράριο',
					scheduleOperateEveryday?.open ? `από ${formatHM(willStartOperatingAt)}` : null,
					scheduleOperateEveryday?.close ? `έως ${formatHM(willStopOperatingAt)}` : null,
				]
					.filter(Boolean)
					.join(' ')
			: '';

	// Only when paused, and only if somebody wrote one — this is the only thing
	// on /live that says why the form has gone, so an empty reason must not eat
	// the notice, but it must not draw an empty box either.
	const pauseReason = !isOperating ? notOperatingReason || '' : '';
	// console.log('isWithinNext5Min', isWithinNext5Min);
	// console.log('isWithinNext20Min', isWithinNext20Min);

	return (
		<>
			{(selectedClient || asSimpleOrder) && (
				<>
					{requiredOrderFields?.price?.mode && (
						<div className='flex flex-row self-stretch'>
							<FloatingTextinput
								value={price}
								label='Τιμή'
								onChange={e => setPrice(e)}
								onBlur={validatePrice}
								error={errorPrice}
								errorText='Η τιμή πρέπει να είναι νούμερο'
								size={size.width > 600 ? 'xl' : 'full'}
								cnRightComponents='top-1.5'
								cnRightComponentLine='mt-1'
								rightComponents={[
									{
										key: 'credit-card-button',
										onClick: () => {
											setPaymentType(s =>
												s === PAYMENT_TYPES.CARD ? PAYMENT_TYPES.CASH : PAYMENT_TYPES.CARD,
											);
										},
										component: (
											<div
												className='ml-0.5 flex items-center justify-center rounded-md p-1.5'
												style={{
													backgroundColor:
														paymentType === PAYMENT_TYPES.CARD
															? twColors.slate[300]
															: 'transparent',
												}}
											>
												<CreditCard color={twColors.slate[600]} size={18} />
											</div>
										),
									},
								]}
							/>
						</div>
					)}

					<FloatingTextinput
						label='Σχόλια παραγγελίας'
						value={comments}
						onChange={e => setComments(e)}
						size='xl'
						multiline
						styleContainer={{ marginTop: 20 }}
					/>

					{/* «Αυτόματα από το στίγμα»: the area is read off the pin. */}
					{isAutoArea && (
						<div
							className={cn(
								'mt-4 self-start text-[14px]',
								pinArea === '' || pinArea === null ? 'text-red-500' : 'text-slate-600',
							)}
						>
							{pinArea === undefined
								? 'Περιοχή: …'
								: pinArea === ''
									? 'Το στίγμα είναι εκτός περιοχών εξυπηρέτησης'
									: pinArea === null
										? 'Η διεύθυνση δεν έχει στίγμα στον χάρτη'
										: `Περιοχή: ${pinArea}`}
						</div>
					)}

					{areas?.length &&
					requiredOrderFields?.area?.mode &&
					!(isAutoArea && !isMasterOrAdmin) ? (
						<>
							<div className={`${s.areasContainer} ${s.areaContainerMaxWidth}`}>
								{areas.map((area, idx) => (
									<Chip
										key={area}
										label={area}
										onClick={() => {
											setSelectedAreaIdx(idx);
											setAreaPriceProm(selectedShop?.feeList?.[idx] ?? '');
											setAreaShopFee(selectedShop?.shopFeeList?.[idx] ?? '');
										}}
										selected={selectedAreaIdx === idx}
									/>
								))}
							</div>
							{isMasterOrAdmin && (
								<FloatingTextinput
									label='Προμ.'
									value={areaPriceProm}
									onChange={e => setAreaPriceProm(e)}
									// size='sm'
								/>
							)}
							{/* A shop with a fixed charge: the server prices the order from it, so
							    there is nothing to type here. */}
							{isMasterOrAdmin &&
								canSeeShopFees(user) &&
								(user?.cityLocation?.withFixedShopFee && selectedShop?.fixedShopFee > 0 ? (
									<div>Σταθερή χρέωση: {selectedShop.fixedShopFee} €</div>
								) : (
									<FloatingTextinput
										label='Χρέωση καταστ.'
										value={areaShopFee}
										onChange={e => setAreaShopFee(e)}
										// size='sm'
									/>
								))}
						</>
					) : null}

					<DelaySelector
						delay={delay}
						delayRef={delayRef}
						onDelaySelect={onDelaySelect}
						delayOptionsFiltered={delayOptionsFiltered}
					/>

					{isOperating
						? (selectedShop || user?.role === 'shop') && (
								<BottomButtons
									text='Αποστολή'
									onSave={submitOrder}
									saveDisabled={saveDisabled()}
									loading={isSendingOrder}
									style={{ marginTop: 12, marginBottom: 10 }}
								/>
							)
						: null}
				</>
			)}

			{scheduleNotice || pauseReason ? (
				<div className={s.notOperatingLabel}>
					{scheduleNotice && (
						<div
							className={cn(
								'text-sm',
								!isOperating
									? 'text-red-500'
									: stopIsWithinNext5Min
										? 'text-orange-400'
										: stopIsWithinNext20Min
											? 'text-slate-400/40'
											: '',
							)}
						>
							{scheduleNotice}
						</div>
					)}
					{pauseReason}
				</div>
			) : null}
		</>
	);
};
